import React from 'react';

interface NoDataStateProps {
  message?: string;
  suggestion?: string;
  type?: 'search' | 'data' | 'filter' | 'error';
  onRetry?: () => void;
  showSuggestions?: boolean;
  showExtras?: boolean;
}

export const NoDataState: React.FC<NoDataStateProps> = ({ 
  message = "No data found", 
  suggestion,
  type = 'data',
  onRetry,
  showSuggestions = true,
  showExtras = true 
}) => {
  const getIcon = () => {
    switch (type) {
      case 'search':
        return 'search_off';
      case 'filter':
        return 'filter_list_off';
      case 'error':
        return 'error_outline';
      default:
        return 'data_object';
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'search':
        return 'No Results Found';
      case 'filter':
        return 'No Matching Records';
      case 'error':
        return 'Something Went Wrong';
      default:
        return 'No Data Available';
    }
  };

  const getDescription = () => {
    if (message !== "No data found") return message;
    
    switch (type) {
      case 'search':
        return 'Your search didn\'t match any records. Try adjusting your search terms.';
      case 'filter':
        return 'No records match your current filters. Try broadening your criteria.';
      case 'error':
        return 'We encountered an issue while fetching your data.';
      default:
        return 'The query returned no results. This might be due to the specific criteria or time range.';
    }
  };

  const getSuggestions = () => {
    if (!showSuggestions) return [];
    
    switch (type) {
      case 'search':
        return [
          'Check spelling and try again',
          'Use broader search terms',
          'Remove some filters'
        ];
      case 'filter':
        return [
          'Clear some filters',
          'Try different date ranges',
          'Broaden your criteria'
        ];
      case 'error':
        return [
          'Check your connection',
          'Try refreshing the page',
          'Contact support if issue persists'
        ];
      default:
        return [
          'Try a different time period',
          'Check if data exists for this query',
          'Modify your search criteria',
          'Contact admin for data availability'
        ];
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
      {/* Animated Icon */}
      <div className="relative mb-6">
        <div className="w-24 h-24 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center animate-pulse">
          <span 
            className="material-symbols-outlined text-slate-400 dark:text-slate-500" 
            style={{ fontSize: '48px' }}
          >
            {getIcon()}
          </span>
        </div>
        {/* Floating particles animation */}
        <div className="absolute -top-2 -right-2 w-3 h-3 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
        <div className="absolute -top-1 -left-3 w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute -bottom-2 left-1 w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">
        {getTitle()}
      </h3>

      {/* Description */}
      <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md leading-relaxed">
        {getDescription()}
      </p>

      {/* Custom suggestion if provided */}
      {suggestion && showExtras && (
        <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-blue-700 dark:text-blue-300 text-sm">
            💡 <strong>Tip:</strong> {suggestion}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        {onRetry && (
          <button
            onClick={onRetry}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
              refresh
            </span>
            <span>Try Again</span>
          </button>
        )}
        
        {showExtras && (
          <button
            onClick={() => window.history.back()}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-all duration-200"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
              arrow_back
            </span>
            <span>Go Back</span>
          </button>
        )}
      </div>

      {/* Suggestions List */}
      {showSuggestions && showExtras && getSuggestions().length > 0 && (
        <div className="max-w-sm">
          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            Try these suggestions:
          </h4>
          <ul className="space-y-2">
            {getSuggestions().map((suggestion, index) => (
              <li key={index} className="flex items-start space-x-2 text-sm text-slate-500 dark:text-slate-400">
                <span className="material-symbols-outlined text-green-500 mt-0.5" style={{ fontSize: '16px' }}>
                  check_circle
                </span>
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Decorative Element */}
      {/* <div className="mt-8 opacity-20">
        <div className="flex space-x-2">
          <div className="w-2 h-2 bg-slate-300 dark:bg-slate-600 rounded-full animate-pulse" style={{ animationDelay: '0s' }}></div>
          <div className="w-2 h-2 bg-slate-300 dark:bg-slate-600 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-2 h-2 bg-slate-300 dark:bg-slate-600 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
        </div>
      </div> */}
    </div>
  );
};